import { useEffect, useState } from "react";
import "./Header.scss";

const NavLink = ({
    path,
    text,
    activeSection,
    onNavigate
}: {
    path: string;
    text: string;
    activeSection: string;
    onNavigate?: () => void;
}) => {
    const sectionId = path.replace("#", "");
    const isActive = activeSection === sectionId;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const element = document.querySelector(path);
        if (element) {
            const headerHeight = 100; // Approximate header height when sticky
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            if (onNavigate) {
                onNavigate();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as any);
        }
    };

    return (
        <a
            href={path}
            className={`nav-link ${isActive ? "active" : ""}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-current={isActive ? "page" : undefined}
        >
            {text}
        </a>
    );
};

const Header = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [open, setOpen] = useState(false);
    const [shrink, setShrink] = useState(false);
    const [activeSection, setActiveSection] = useState<string>("");

    useEffect(() => {
        const sections = document.querySelectorAll(".section");
        
        const updateActiveSection = () => {
            const scrollPosition = window.scrollY + 150; // Offset for header
            
            let currentSection = "";
            let minDistance = Infinity;

            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                const sectionTop = rect.top + window.scrollY;
                const sectionBottom = sectionTop + rect.height;
                
                // Check if section is in viewport
                if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
                    const distance = Math.abs(scrollPosition - sectionTop);
                    if (distance < minDistance) {
                        minDistance = distance;
                        currentSection = section.id;
                    }
                }
            });

            // If no section found, find the closest one
            if (!currentSection) {
                sections.forEach(section => {
                    const rect = section.getBoundingClientRect();
                    const sectionTop = rect.top + window.scrollY;
                    const distance = Math.abs(scrollPosition - sectionTop);
                    if (distance < minDistance) {
                        minDistance = distance;
                        currentSection = section.id;
                    }
                });
            }

            if (currentSection) {
                setActiveSection(currentSection);
            }
        };

        // Initial check
        updateActiveSection();

        // Update on scroll
        const handleScroll = () => updateActiveSection();
        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShrink(prev => {
                if (!prev && window.scrollY > 150) return true;   // shrink when scrolling down
                if (prev && window.scrollY < 80) return false;    // unshrink only when far up
                return prev;
            });
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Detect viewport width
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 900);
        };

        handleResize(); // initialize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleMenu = () => {
        setOpen(!open);
    };

    // Handle escape key to close mobile menu
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && open && isMobile) {
                setOpen(false);
            }
        };

        if (open) {
            window.addEventListener('keydown', handleEscape);
            return () => window.removeEventListener('keydown', handleEscape);
        }
    }, [open, isMobile]);

    const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <header className={shrink ? "shrink" : ""}>
            <a href="/" id="logo" aria-label="Home - Chloe Pappas" onClick={handleLogoClick}>
                <i className="bi bi-suit-spade-fill" aria-hidden="true"></i>
            </a>

            {/* Hamburger button (visible only on mobile) */}
            {isMobile && (
                <button
                    className={`hamburger ${open ? "open" : ""}`}
                    onClick={toggleMenu}
                    aria-label="Toggle navigation"
                    aria-expanded={open}
                    aria-controls="main-nav"
                >
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                </button>
            )}

            {/* Navigation */}
            <nav id="main-nav" className={isMobile ? (open ? "open" : "closed") : ""} aria-label="Main navigation">
                <NavLink path="#about" text="About" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
                <NavLink path="#education" text="Education" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
                <NavLink path="#experience" text="Experience" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
                <NavLink path="#projects" text="Projects" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
                <NavLink path="#travel" text="Travel" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
                <NavLink path="#contact" text="Contact" activeSection={activeSection} onNavigate={() => isMobile && setOpen(false)} />
            </nav>
        </header>
    );
};

export default Header;
